import { set, get, del, keys } from 'idb-keyval';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

const isBrowser = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
const CACHE_PREFIX = 'app:chat_cache:';
const UPLOAD_PREFIX = 'app:upload:';
const ID_ALLOWLIST = /^[a-zA-Z0-9:_-]{1,128}$/;
const MAX_ITEM_SIZE_BYTES = 1 * 1024 * 1024; // 1MB per cache item
const MAX_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024; // 5MB per offline upload

// ⚡️ PERFORMANCE OPTIMIZATION: Advanced offline storage with IndexedDB
class OfflineStorage {
  constructor() {
    this.CACHE_PREFIX = CACHE_PREFIX;
    this.CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
    this.MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Cache messages with metadata
  async cacheMessages(conversationId, messages) {
    try {
      if (!ID_ALLOWLIST.test(String(conversationId))) {
        safeReportError('offline_cache_invalid_conversation_id');
        return;
      }
      const cacheKey = `${this.CACHE_PREFIX}${conversationId}`;
      const cacheData = {
        messages,
        timestamp: Date.now(),
        version: '1.0',
        messageCount: messages.length
      };
      // Size cap to avoid oversized entries
      try {
        const size = JSON.stringify(cacheData).length;
        if (size > MAX_ITEM_SIZE_BYTES) {
          safeReportError('offline_cache_item_too_large', { size });
          return;
        }
      } catch (_) {}
      
      if (!isBrowser) return;
      await set(cacheKey, cacheData);
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Cleanup old cache entries
      await this.cleanupOldCache();
    } catch (error) {
      safeReportError('offline_cache_messages_failed');
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Get cached messages with validation
  async getCachedMessages(conversationId) {
    try {
      if (!ID_ALLOWLIST.test(String(conversationId))) return null;
      if (!isBrowser) return null;
      const cacheKey = `${this.CACHE_PREFIX}${conversationId}`;
      const cacheData = await get(cacheKey);
      
      if (!cacheData) return null;
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Check cache expiry
      const isExpired = Date.now() - cacheData.timestamp > this.CACHE_EXPIRY;
      if (isExpired) {
        await this.clearCache(conversationId);
        return null;
      }
      
      // Messages loaded from cache
      return cacheData.messages;
    } catch (error) {
      safeReportError('offline_get_cached_messages_failed');
      return null;
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Clear specific conversation cache
  async clearCache(conversationId) {
    try {
      if (!ID_ALLOWLIST.test(String(conversationId))) return;
      if (!isBrowser) return;
      const cacheKey = `${this.CACHE_PREFIX}${conversationId}`;
      await del(cacheKey);
      // Cache cleared
    } catch (error) {
      safeReportError('offline_clear_cache_failed');
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Cleanup old cache entries
  async cleanupOldCache() {
    try {
      if (!isBrowser) return;
      const allKeys = await keys();
      const cacheKeys = allKeys.filter(key => key.startsWith(this.CACHE_PREFIX));
      
      let totalSize = 0;
      const cacheEntries = [];
      
      // Calculate total cache size
      for (const key of cacheKeys) {
        const data = await get(key);
        if (data) {
          const size = JSON.stringify(data).length;
          totalSize += size;
          cacheEntries.push({ key, data, size, timestamp: data.timestamp });
        }
      }
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Remove old entries if cache is too large
      if (totalSize > this.MAX_CACHE_SIZE) {
        // Sort by timestamp (oldest first)
        cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
        
        // Remove oldest entries until we're under the limit
        for (const entry of cacheEntries) {
          await del(entry.key);
          totalSize -= entry.size;
          
          if (totalSize <= this.MAX_CACHE_SIZE * 0.8) { // Keep 20% buffer
            break;
          }
        }
        
        // Cache cleaned up
      }
    } catch (error) {
      safeReportError('offline_cleanup_failed');
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Cache file uploads for offline retry
  async cacheFailedUpload(file, conversationId) {
    try {
      if (!ID_ALLOWLIST.test(String(conversationId))) {
        safeReportError('offline_upload_invalid_conversation_id');
        return;
      }
      if (!isBrowser) return;
      const uploadKey = `${UPLOAD_PREFIX}${conversationId}_${Date.now()}`;
      const uploadData = {
        file: await this.fileToBase64(file),
        fileName: file.name,
        fileType: file.type,
        conversationId,
        timestamp: Date.now()
      };
      // Enforce upload size cap
      try {
        const approxSize = typeof uploadData.file === 'string' ? uploadData.file.length : JSON.stringify(uploadData).length;
        if (approxSize > MAX_UPLOAD_SIZE_BYTES) {
          safeReportError('offline_upload_item_too_large', { size: approxSize });
          return;
        }
      } catch (_) {}
      
      await set(uploadKey, uploadData);
      // Failed upload cached
    } catch (error) {
      safeReportError('offline_cache_upload_failed');
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Get failed uploads for retry
  async getFailedUploads(conversationId) {
    try {
      if (!ID_ALLOWLIST.test(String(conversationId))) return [];
      if (!isBrowser) return [];
      const allKeys = await keys();
      const prefix = `${UPLOAD_PREFIX}${conversationId}_`;
      const uploadKeys = allKeys.filter(key => typeof key === 'string' && key.startsWith(prefix));
      
      const failedUploads = [];
      for (const key of uploadKeys) {
        const uploadData = await get(key);
        if (uploadData) {
          failedUploads.push(uploadData);
        }
      }
      
      return failedUploads;
    } catch (error) {
      safeReportError('offline_get_failed_uploads_failed');
      return [];
    }
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Convert file to base64 for storage
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Convert base64 back to file
  base64ToFile(base64, fileName, fileType) {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    return new File([ab], fileName, { type: fileType });
  }

  // ⚡️ PERFORMANCE OPTIMIZATION: Get cache statistics
  async getCacheStats() {
    try {
      if (!isBrowser) return null;
      const allKeys = await keys();
      const cacheKeys = allKeys.filter(key => key.startsWith(this.CACHE_PREFIX));
      
      let totalMessages = 0;
      let totalSize = 0;
      
      for (const key of cacheKeys) {
        const data = await get(key);
        if (data) {
          totalMessages += data.messageCount || 0;
          totalSize += JSON.stringify(data).length;
        }
      }
      
      return {
        conversationCount: cacheKeys.length,
        totalMessages,
        totalSize: `${(totalSize / 1024 / 1024).toFixed(2)}MB`
      };
    } catch (error) {
      safeReportError('offline_get_cache_stats_failed');
      return null;
    }
  }
}

// ⚡️ PERFORMANCE OPTIMIZATION: Export singleton instance
export const offlineStorage = new OfflineStorage();
export default offlineStorage; 